const PLACEHOLDER_MARKERS = ["/placeholder.png", "/placeholder.svg", "default.png"];
const QIT_AVATAR_TOKEN_PREFIX = "qit-avatar:";

const AVATAR_PALETTES = [
    { start: "#0f766e", end: "#14b8a6" },
    { start: "#1d4ed8", end: "#38bdf8" },
    { start: "#b45309", end: "#f59e0b" },
    { start: "#7c3aed", end: "#c084fc" },
    { start: "#be123c", end: "#fb7185" },
    { start: "#1f2937", end: "#64748b" },
];

const SKIN_TONES = ["#f8d7c2", "#efc2a4", "#d79d72", "#b8754f", "#8e5638"];
const HAIR_COLORS = ["#1f2937", "#5b4636", "#8c5a3c", "#4c1d95", "#7f1d1d", "#0f172a"];

const AVATAR_VARIANTS = [
    "circle",
    "rounded-square",
    "diamond",
    "hex",
    "shield",
    "orbit",
    "ripple",
    "visor",
    "flare",
    "halo",
    "tuft",
    "spike",
] as const;

type AvatarVariant = (typeof AVATAR_VARIANTS)[number];

const normalizeText = (value: unknown): string =>
    typeof value === "string" ? value.trim() : "";

const escapeXml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");

const isMeaningfulAvatar = (value: string): boolean => {
    if (!value) return false;
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    return !PLACEHOLDER_MARKERS.some((marker) => normalized.includes(marker));
};

const isDirectAvatarSource = (value: string): boolean => {
    const normalized = value.toLowerCase();
    return (
        normalized.startsWith("https://") ||
        normalized.startsWith("http://") ||
        normalized.startsWith("data:image/")
    );
};

const hashValue = (value: string): number => {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
        hash = (hash << 5) - hash + value.charCodeAt(index);
        hash |= 0;
    }
    return Math.abs(hash);
};

const getPalette = (seed: string) => AVATAR_PALETTES[hashValue(seed) % AVATAR_PALETTES.length];

const isAvatarVariant = (value: string): value is AvatarVariant =>
    (AVATAR_VARIANTS as readonly string[]).includes(value);

const normalizeVariant = (value?: string | null): AvatarVariant => {
    const normalized = normalizeText(value).toLowerCase();
    return isAvatarVariant(normalized) ? normalized : "circle";
};

const parseAvatarToken = (value: string): AvatarVariant | null => {
    if (!value.startsWith(QIT_AVATAR_TOKEN_PREFIX)) return null;
    const tokenValue = normalizeText(value.slice(QIT_AVATAR_TOKEN_PREFIX.length)).toLowerCase();
    if (!tokenValue || tokenValue === "auto" || tokenValue === "default") {
        return null;
    }
    return normalizeVariant(tokenValue);
};

const pickFrom = <T,>(seed: string, values: readonly T[]): T =>
    values[hashValue(seed) % values.length];

const adjustHexColor = (hex: string, amount: number): string => {
    const safeHex = hex.replace("#", "");
    const numeric = parseInt(safeHex, 16);
    const clamp = (value: number) => Math.max(0, Math.min(255, value));

    const red = clamp((numeric >> 16) + amount);
    const green = clamp(((numeric >> 8) & 0xff) + amount);
    const blue = clamp((numeric & 0xff) + amount);

    return `#${[red, green, blue].map((part) => part.toString(16).padStart(2, "0")).join("")}`;
};

const resolveDefaultVariant = (seed: string): AvatarVariant =>
    AVATAR_VARIANTS[hashValue(`${seed}:variant`) % AVATAR_VARIANTS.length];

const buildAvatarToken = (variant: string): string =>
    `${QIT_AVATAR_TOKEN_PREFIX}${normalizeVariant(variant)}`;

export { buildAvatarToken };

export const getAvatarInitials = (value?: string, fallback = "Q"): string => {
    const normalized = normalizeText(value);
    if (!normalized) return fallback.slice(0, 2).toUpperCase();

    const words = normalized
        .split(/\s+/)
        .map((part) => part.replace(/[^a-zA-Z0-9]/g, ""))
        .filter(Boolean);

    if (words.length === 0) return fallback.slice(0, 2).toUpperCase();
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

    return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
};

const buildHairSvg = (
    variant: AvatarVariant,
    hairColor: string,
    accentColor: string
): string => {
    switch (variant) {
        case "ripple":
            return `
                <path d="M22 41c3-14 13-22 26-22 13 0 23 8 26 22v8c-6-6-15-9-26-9-12 0-20 3-26 9z" fill="${hairColor}" />
                <path d="M24 35c4-6 9-9 15-9 6 0 10 2 14 7 3-4 7-6 12-6 3 0 6 1 10 3v5c-4-2-6-3-9-3-5 0-9 3-13 8-4-5-8-8-14-8-5 0-10 2-15 6z" fill="${adjustHexColor(hairColor, 16)}" />
            `;
        case "visor":
            return `
                <path d="M24 39c4-13 13-20 24-20 11 0 20 7 24 19v8c-6-5-14-8-24-8s-18 3-24 8z" fill="${hairColor}" />
                <rect x="22" y="34" width="52" height="9" rx="4.5" fill="${adjustHexColor(hairColor, -10)}" />
                <path d="M30 34c3-3 8-5 18-5s15 2 18 5" stroke="${accentColor}" stroke-width="1.2" fill="none" />
            `;
        case "flare":
            return `
                <path d="M22 43c5-16 16-24 29-24 11 0 19 4 24 12l-8 3c-5-5-10-7-16-7-12 0-21 6-29 16z" fill="${hairColor}" />
                <path d="M30 26c8-7 21-9 34-4l3 7c-8-2-15-2-23 0-5 2-9 4-14 8z" fill="${adjustHexColor(hairColor, 12)}" />
                <path d="M23 48c4-6 9-10 15-12v20c-5-2-10-4-15-8z" fill="${hairColor}" />
            `;
        case "halo":
            return `
                <ellipse cx="48" cy="34" rx="27" ry="16" fill="${adjustHexColor(hairColor, 10)}" />
                <path d="M22 45c2-14 12-22 26-22 14 0 24 8 26 22v5c-6-6-15-10-26-10s-20 4-26 10z" fill="${hairColor}" />
                <circle cx="22" cy="46" r="4" fill="${adjustHexColor(hairColor, 8)}" />
                <circle cx="74" cy="46" r="4" fill="${adjustHexColor(hairColor, 8)}" />
            `;
        case "tuft":
            return `
                <path d="M27 44c2-14 11-22 21-22 10 0 19 8 21 21v8c-5-5-12-8-21-8s-16 3-21 8z" fill="${hairColor}" />
                <path d="M40 23l8-9 8 9-3 19h-10z" fill="${adjustHexColor(hairColor, 20)}" />
                <path d="M38 42c2-3 5-5 10-5s8 2 10 5" stroke="${adjustHexColor(hairColor, -8)}" stroke-width="1.2" fill="none" />
            `;
        case "spike":
            return `
                <path d="M22 42c3-14 13-23 26-23 14 0 24 8 26 22v8c-7-6-16-9-26-9s-19 3-26 9z" fill="${hairColor}" />
                <path d="M28 33l5-10 6 9 5-11 6 10 6-9 5 11 5-9 4 10-5 5H33z" fill="${adjustHexColor(hairColor, 14)}" />
            `;
        case "rounded-square":
            return `
                <path d="M28 38c1-15 11-22 21-22 11 0 19 7 21 21v10c-4-6-12-10-22-10-7 0-14 2-20 6z" fill="${hairColor}" />
                <path d="M24 43c2-12 11-19 24-19 11 0 20 7 24 19-5-3-11-5-17-5-8 0-14 2-20 5-4-2-7-3-11-4z" fill="${adjustHexColor(hairColor, 10)}" />
                <path d="M23 56c0-8 4-15 10-20v32c-6-4-10-8-10-12zM73 56c0-8-4-15-10-20v32c6-4 10-8 10-12z" fill="${hairColor}" />
            `;
        case "diamond":
            return `
                <path d="M23 42c4-15 15-23 28-23 12 0 20 6 24 15l-9 2c-2-5-8-8-15-8-11 0-20 6-28 14z" fill="${hairColor}" />
                <path d="M26 40c4-12 14-18 24-18 11 0 19 5 23 14-6-1-9 3-13 8-4-5-10-8-18-8-6 0-11 1-16 4z" fill="${adjustHexColor(hairColor, 18)}" />
            `;
        case "hex":
            return `
                <path d="M25 40c2-14 12-22 23-22 12 0 22 8 23 22v7c-5-6-13-9-23-9-9 0-17 3-23 9z" fill="${hairColor}" />
                <path d="M27 58c-4 4-6 10-5 15 2 0 5-2 7-5l2-7-4-3zM69 58c4 4 6 10 5 15-2 0-5-2-7-5l-2-7 4-3z" fill="${adjustHexColor(hairColor, -12)}" />
                <circle cx="25" cy="77" r="4" fill="${accentColor}" />
                <circle cx="71" cy="77" r="4" fill="${accentColor}" />
            `;
        case "shield":
            return `
                <path d="M24 42c2-15 11-24 24-24 13 0 21 8 24 20v8c-7-7-15-10-24-10-9 0-17 2-24 6z" fill="${hairColor}" />
                <path d="M26 36c4-11 12-16 22-16 10 0 18 5 22 15l-8 3c-4-3-8-4-13-4-7 0-13 1-23 2z" fill="${adjustHexColor(hairColor, 12)}" />
                <path d="M24 31c5-6 14-10 24-10 11 0 20 4 25 11v4H24z" fill="${accentColor}" />
            `;
        case "orbit":
            return `
                <path d="M25 33c6-8 14-12 23-12 10 0 18 4 24 12v8H25z" fill="${accentColor}" />
                <path d="M28 38c1-12 10-19 21-19 11 0 20 6 21 18v10c-5-4-12-7-21-7-8 0-15 2-21 6z" fill="${hairColor}" />
                <rect x="24" y="29" width="48" height="10" rx="5" fill="${adjustHexColor(accentColor, -8)}" />
            `;
        case "circle":
        default:
            return `
                <path d="M24 42c2-15 12-23 24-23 13 0 22 8 24 23v7c-6-7-14-10-24-10-10 0-18 3-24 10z" fill="${hairColor}" />
                <circle cx="31" cy="31" r="6" fill="${adjustHexColor(hairColor, 10)}" />
                <circle cx="65" cy="31" r="6" fill="${adjustHexColor(hairColor, 10)}" />
            `;
    }
};

const buildAccessorySvg = (
    variant: AvatarVariant,
    accentColor: string,
    strokeColor: string
): string => {
    switch (variant) {
        case "visor":
            return `
                <rect x="35" y="47" width="26" height="8" rx="4" fill="rgba(255,255,255,0.16)" />
                <path d="M35 51h26" stroke="${strokeColor}" stroke-width="1.2" />
                <path d="M40 61c2 1 5 2 8 2 4 0 7-1 9-2" stroke="${strokeColor}" stroke-width="1.3" stroke-linecap="round" fill="none" />
            `;
        case "flare":
            return `
                <path d="M34 54c3-2 8-3 14-3 6 0 11 1 14 3" stroke="${strokeColor}" stroke-width="1.4" stroke-linecap="round" fill="none" />
                <circle cx="32" cy="56" r="1.6" fill="${accentColor}" />
                <circle cx="64" cy="56" r="1.6" fill="${accentColor}" />
            `;
        case "halo":
            return `
                <path d="M38 50h20" stroke="${strokeColor}" stroke-width="1.3" stroke-linecap="round" />
                <ellipse cx="48" cy="60" rx="10" ry="3.4" fill="none" stroke="${strokeColor}" stroke-width="1.2" />
            `;
        case "tuft":
            return `
                <path d="M39 60c2 2 5 3 9 3 4 0 7-1 9-3" stroke="${strokeColor}" stroke-width="1.5" stroke-linecap="round" fill="none" />
                <rect x="45" y="57" width="6" height="2" rx="1" fill="${accentColor}" />
            `;
        case "spike":
            return `
                <path d="M36 60c2 1 6 2 12 2 6 0 10-1 12-2" stroke="${strokeColor}" stroke-width="1.5" stroke-linecap="round" fill="none" />
                <path d="M43 53h10" stroke="${strokeColor}" stroke-width="1.2" stroke-linecap="round" />
            `;
        case "ripple":
            return `
                <path d="M34 58c3-2 8-3 14-3 6 0 11 1 14 3" stroke="${strokeColor}" stroke-width="1.4" stroke-linecap="round" fill="none" />
                <path d="M40 62c2 1 5 2 8 2 3 0 6-1 8-2" stroke="${accentColor}" stroke-width="1.2" stroke-linecap="round" fill="none" />
            `;
        case "diamond":
            return `
                <path d="M36 56c2-3 5-4 12-4s10 1 12 4" stroke="${strokeColor}" stroke-width="1.5" stroke-linecap="round" fill="none" />
            `;
        case "hex":
            return `
                <path d="M37 60c2 2 5 3 11 3s9-1 11-3" stroke="${strokeColor}" stroke-width="1.5" stroke-linecap="round" fill="none" />
                <circle cx="33" cy="61" r="1.5" fill="${accentColor}" />
                <circle cx="63" cy="61" r="1.5" fill="${accentColor}" />
            `;
        case "shield":
            return `
                <path d="M33 50h30" stroke="${strokeColor}" stroke-width="1.4" stroke-linecap="round" />
                <path d="M40 50c0-4 3-6 8-6s8 2 8 6" stroke="${strokeColor}" stroke-width="1.2" fill="none" />
            `;
        default:
            return `
                <path d="M39 61c2 2 5 3 9 3 5 0 8-1 10-3" stroke="${strokeColor}" stroke-width="1.5" stroke-linecap="round" fill="none" />
            `;
    }
};

const buildCartoonAvatarSvg = ({
    label,
    seed,
    variant,
}: {
    label: string;
    seed: string;
    variant: AvatarVariant;
}): string => {
    const palette = getPalette(seed);
    const skin = pickFrom(`${seed}:skin`, SKIN_TONES);
    const hair = pickFrom(`${seed}:hair`, HAIR_COLORS);
    const shirt = adjustHexColor(palette.start, -10);
    const shirtAccent = adjustHexColor(palette.end, 12);
    const faceShade = adjustHexColor(skin, -12);
    const eye = adjustHexColor(hair, -30);
    const blush = adjustHexColor(skin, 18);
    const backgroundGlow = adjustHexColor(palette.end, 18);

    const hairSvg = buildHairSvg(variant, hair, shirtAccent);
    const accessorySvg = buildAccessorySvg(variant, shirtAccent, eye);

    return `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" role="img" aria-label="${escapeXml(label)}">
            <defs>
                <linearGradient id="avatar-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="${palette.start}" />
                    <stop offset="100%" stop-color="${palette.end}" />
                </linearGradient>
                <radialGradient id="avatar-glow" cx="50%" cy="28%" r="70%">
                    <stop offset="0%" stop-color="${backgroundGlow}" stop-opacity="0.55" />
                    <stop offset="100%" stop-color="${backgroundGlow}" stop-opacity="0" />
                </radialGradient>
            </defs>

            <rect width="96" height="96" rx="28" fill="url(#avatar-gradient)" />
            <circle cx="48" cy="22" r="30" fill="url(#avatar-glow)" />
            <circle cx="17" cy="20" r="5" fill="rgba(255,255,255,0.15)" />
            <circle cx="76" cy="15" r="3" fill="rgba(255,255,255,0.14)" />
            <path d="M8 87c10-8 24-13 40-13s30 5 40 13v9H8z" fill="rgba(255,255,255,0.14)" />

            <g>
                <path d="M22 90c2-12 11-22 26-22s24 10 26 22z" fill="${shirt}" />
                <path d="M35 72c4 5 8 8 13 8 5 0 9-3 13-8 6 3 10 8 13 18H22c3-10 7-15 13-18z" fill="${shirtAccent}" opacity="0.85" />
                <rect x="43" y="60" width="10" height="12" rx="4" fill="${faceShade}" />

                <ellipse cx="48" cy="47" rx="18" ry="20" fill="${skin}" />
                <ellipse cx="31" cy="49" rx="3" ry="6" fill="${skin}" />
                <ellipse cx="65" cy="49" rx="3" ry="6" fill="${skin}" />
                ${hairSvg}

                <ellipse cx="41" cy="49" rx="1.9" ry="2.2" fill="${eye}" />
                <ellipse cx="55" cy="49" rx="1.9" ry="2.2" fill="${eye}" />
                <path d="M37 44c2-2 4-3 8-3" stroke="${adjustHexColor(hair, -16)}" stroke-width="1.5" stroke-linecap="round" fill="none" />
                <path d="M51 41c4 0 6 1 8 3" stroke="${adjustHexColor(hair, -16)}" stroke-width="1.5" stroke-linecap="round" fill="none" />
                <ellipse cx="38" cy="56" rx="3.4" ry="2.2" fill="${blush}" opacity="0.35" />
                <ellipse cx="58" cy="56" rx="3.4" ry="2.2" fill="${blush}" opacity="0.35" />
                <path d="M46 50c0 2 0 4 2 5 2-1 2-3 2-5" stroke="${faceShade}" stroke-width="1.2" fill="none" stroke-linecap="round" />
                ${accessorySvg}
            </g>
        </svg>
    `.trim();
};

export const buildGeneratedAvatarDataUrl = ({
    label,
    seed,
    variant,
}: {
    label?: string;
    seed: string;
    variant?: string | null;
}): string => {
    const avatarVariant = variant ? normalizeVariant(variant) : resolveDefaultVariant(seed);
    const svg = buildCartoonAvatarSvg({
        label: label || "User avatar",
        seed,
        variant: avatarVariant,
    });
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

export const resolveUserAvatarSrc = ({
    src,
    avatar,
    avatarUrl,
    name,
    userId,
    email,
}: {
    src?: string | null;
    avatar?: string | null;
    avatarUrl?: string | null;
    name?: string;
    userId?: string;
    email?: string;
}): string => {
    const avatarCandidates = [src, avatar, avatarUrl]
        .map((candidate) => normalizeText(candidate))
        .filter(isMeaningfulAvatar);

    const directAvatarSource = avatarCandidates.find(isDirectAvatarSource);
    if (directAvatarSource) return directAvatarSource;

    const savedAvatarTokens = avatarCandidates
        .map((candidate) => parseAvatarToken(candidate))
        .filter((candidate): candidate is AvatarVariant => Boolean(candidate));

    const normalizedEmail = normalizeText(email);
    const fallbackName = normalizeText(name) || normalizedEmail.split("@")[0] || "User";
    const seed = normalizeText(userId) || normalizedEmail || fallbackName.toLowerCase() || "qit-user";

    for (const variant of savedAvatarTokens) {
        return buildGeneratedAvatarDataUrl({
            label: fallbackName,
            seed,
            variant,
        });
    }

    return buildGeneratedAvatarDataUrl({
        label: fallbackName,
        seed,
    });
};
